import { Check, ChevronDown, CircleAlert, Loader2, ShieldCheck, Wrench } from 'lucide-react'
import type { ReactNode } from 'react'
import { Button } from '../ui'

export type ToolPartView = {
  type: string
  state: string
  toolCallId?: string
  input?: unknown
  output?: unknown
  errorText?: string
  approval?: { id: string; approved?: boolean; reason?: string }
}

function toolLabel(type: string) {
  return type.replace(/^tool-/, '').replace(/[-_]/g, ' ')
}

export function Tool({ children, defaultOpen = false }: { children: ReactNode; defaultOpen?: boolean }) {
  return <details open={defaultOpen} className="rounded-xl border border-panel-border/70 bg-panel-raised/60">{children}</details>
}

export function ToolHeader({ type, state, title }: { type: string; state: string; title?: string }) {
  const Icon = state === 'output-error' ? CircleAlert : state === 'output-available' ? Check : state === 'approval-requested' ? ShieldCheck : Wrench
  return <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2.5 text-xs font-medium text-ink [&::-webkit-details-marker]:hidden"><Icon className="h-3.5 w-3.5 text-ink-muted" /> <span className="capitalize">{title || toolLabel(type)}</span><span className="ml-auto flex items-center gap-1.5 text-[10px] font-normal text-ink-faint">{state === 'input-available' || state === 'input-streaming' ? <Loader2 className="h-3 w-3 animate-spin" /> : null}<span>{state.replaceAll('-', ' ')}</span><ChevronDown className="h-3 w-3" /></span></summary>
}

export function ToolContent({ children }: { children: ReactNode }) { return <div className="space-y-2 border-t border-panel-border/60 px-3 py-3">{children}</div> }

export function ToolInput({ input }: { input: unknown }) { return <pre className="max-h-40 overflow-auto rounded-lg bg-panel-muted/60 p-2 text-[11px] leading-relaxed text-ink-muted">{JSON.stringify(input ?? {}, null, 2)}</pre> }

export function ToolOutput({ output, errorText }: { output?: unknown; errorText?: string }) {
  return <div className={`rounded-lg p-2 text-xs ${errorText ? 'bg-accent-danger/5 text-accent-danger' : 'bg-accent-success/5 text-ink-muted'}`}>{errorText || (output == null ? 'Waiting for output…' : <pre className="max-h-48 overflow-auto whitespace-pre-wrap">{JSON.stringify(output, null, 2)}</pre>)}</div>
}

export function ToolApproval({ onApprove, onDeny, disabled = false }: { onApprove: () => void; onDeny?: () => void; disabled?: boolean }) {
  return <div className="flex items-center gap-2 pt-1"><Button variant="ghost" className="h-8 border border-accent-success/40 px-3 text-xs" disabled={disabled} onClick={onApprove}>Approve</Button>{onDeny ? <Button variant="ghost" className="h-8 border border-panel-border px-3 text-xs" disabled={disabled} onClick={onDeny}>Deny</Button> : null}</div>
}
